<template>
  <div class="animated fadeIn">

    <!-- Page Header -->
    <b-row class="mb-3">
      <b-col>
        <b-button variant="outline-secondary" size="sm" @click="$router.back()">
          <i class="fa fa-arrow-left"></i> Back
        </b-button>
        <span class="ml-3 page-heading">
          <strong>Edit Sensor</strong>
          <span v-if="sensor" class="text-muted"> — {{ sensor.label }}</span>
        </span>
      </b-col>
    </b-row>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading sensor...</p>
    </div>

    <!-- Error -->
    <b-alert v-else-if="loadError" show variant="danger">
      <strong>Error loading sensor:</strong> {{ loadError.message }}
    </b-alert>

    <!-- Content -->
    <template v-else-if="sensor">

      <!-- Device Info Banner -->
      <b-card class="mb-3 device-banner">
        <b-row>
          <b-col md="3" sm="6">
            <div class="banner-field">
              <span class="banner-label">Device</span>
              <span class="banner-value">{{ sensor.config.device.name }}</span>
            </div>
          </b-col>
          <b-col md="3" sm="6">
            <div class="banner-field">
              <span class="banner-label">Serial</span>
              <span class="banner-value">{{ sensor.config.device.serial }}</span>
            </div>
          </b-col>
          <b-col md="3" sm="6">
            <div class="banner-field">
              <span class="banner-label">Type</span>
              <span class="banner-value text-capitalize">{{ sensor.config.device.type }}</span>
            </div>
          </b-col>
          <b-col md="3" sm="6">
            <div class="banner-field">
              <span class="banner-label">Site</span>
              <span class="banner-value">{{ deviceSite }}</span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-row>
        <b-col lg="8" class="mb-4">
          <b-card>
            <div slot="header">
              <i class="fa fa-cog"></i> Sensor Settings
            </div>

            <b-form @submit.prevent="saveSettings">
              <b-row>
                <b-col md="6">
                  <b-form-group label="Label" label-for="s-label">
                    <b-form-select
                      id="s-label"
                      v-model="form.label"
                      :options="labelOptions"
                      required
                    ></b-form-select>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Type" label-for="s-type">
                    <b-form-select
                      id="s-type"
                      v-model="form.type"
                      :options="sensorTypeOptions"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6">
                  <b-form-group label="Unit" label-for="s-unit">
                    <b-form-input
                      id="s-unit"
                      v-model="form.unit"
                      placeholder="e.g. ft, °C, %"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Interface" label-for="s-iface">
                    <b-form-select
                      id="s-iface"
                      v-model="form.iface"
                      :options="interfaceOptions"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="4">
                  <b-form-group label="Channel" label-for="s-channel">
                    <b-form-input
                      id="s-channel"
                      v-model.number="form.channel"
                      type="number"
                      min="0"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Min" label-for="s-min">
                    <b-form-input
                      id="s-min"
                      v-model.number="form.min"
                      type="number"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Max" label-for="s-max">
                    <b-form-input
                      id="s-max"
                      v-model.number="form.max"
                      type="number"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6">
                  <b-form-group label="Sensor Nr" label-for="s-nr">
                    <b-form-input
                      id="s-nr"
                      v-model.number="form.nr"
                      type="number"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Calibration (cal)" label-for="s-cal">
                    <b-form-input
                      id="s-cal"
                      v-model="calInput"
                      type="number"
                      step="0.0001"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Applied offset: calibrated = raw + cal
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="text-right mt-2">
                <b-button type="submit" variant="primary" :disabled="saving">
                  <span v-if="saving">
                    <b-spinner small></b-spinner> Saving...
                  </span>
                  <span v-else>
                    <i class="fa fa-save"></i> Save Settings
                  </span>
                </b-button>
              </div>
            </b-form>
          </b-card>
        </b-col>
      </b-row>

    </template>

  </div>
</template>

<script>
import { DEVICE_SENSOR_QUERY, UPDATE_DEVICE_SENSOR_MUTATION } from '@/constants/graphql'

export default {
  name: 'SensorEdit',
  props: {
    sensorId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      loadError: null,
      sensor: null,
      saving: false,
      calInput: '0',
      form: {
        label: '',
        type: '',
        unit: '',
        iface: '',
        channel: 0,
        min: 0,
        max: 100,
        nr: 1,
        cal: 0
      },
      sensorTypeOptions: [
        { value: '', text: 'Select sensor type...' },
        { value: 'temperature', text: 'Temperature' },
        { value: 'pressure', text: 'Pressure' },
        { value: 'humidity', text: 'Humidity' },
        { value: 'flow', text: 'Flow' },
        { value: 'level', text: 'Level' },
        { value: 'ph', text: 'pH' },
        { value: 'conductivity', text: 'Conductivity' },
        { value: 'turbidity', text: 'Turbidity' },
        { value: 'dissolved_oxygen', text: 'Dissolved Oxygen' },
        { value: 'voltage', text: 'Voltage' },
        { value: 'current', text: 'Current' }
      ],
      labelOptions: [
        { value: '', text: 'Select label...' },
        { value: 'depth', text: 'Depth' },
        { value: 'probe', text: 'Probe' },
        { value: 'battery', text: 'Battery' },
        { value: 'ec', text: 'EC' }
      ],
      interfaceOptions: [
        { value: '', text: 'Select interface...' },
        { value: 'currentLoop', text: 'Current Loop' },
        { value: 'analog', text: 'Analog' },
        { value: 'uart', text: 'UART' },
        { value: 'ezo', text: 'EZO' }
      ]
    }
  },
  computed: {
    deviceSite () {
      return (this.sensor && this.sensor.config.device.site && this.sensor.config.device.site.name) || 'N/A'
    }
  },
  async created () {
    await this.loadSensor()
  },
  methods: {
    async loadSensor () {
      this.loading = true
      this.loadError = null
      try {
        const result = await this.$apollo.query({
          query: DEVICE_SENSOR_QUERY,
          variables: { id: this.sensorId },
          fetchPolicy: 'network-only'
        })
        this.sensor = result.data.deviceSensor
        if (this.sensor) {
          this.form.label = this.sensor.label || ''
          this.form.type = this.sensor.type || ''
          this.form.unit = this.sensor.unit || ''
          this.form.iface = this.sensor.iface || ''
          this.form.channel = this.sensor.channel != null ? this.sensor.channel : 0
          this.form.min = this.sensor.min != null ? this.sensor.min : 0
          this.form.max = this.sensor.max != null ? this.sensor.max : 100
          this.form.nr = this.sensor.nr != null ? this.sensor.nr : 1
          this.form.cal = this.sensor.cal != null ? this.sensor.cal : 0
          this.calInput = String(this.form.cal)
        }
      } catch (err) {
        this.loadError = err
      } finally {
        this.loading = false
      }
    },

    async saveSettings () {
      if (this.saving) return
      this.saving = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_SENSOR_MUTATION,
          variables: {
            id: this.sensor.id,
            input: {
              label: this.form.label,
              type: this.form.type,
              unit: this.form.unit,
              iface: this.form.iface,
              channel: this.form.channel,
              min: this.form.min,
              max: this.form.max,
              nr: this.form.nr,
              cal: parseFloat(this.calInput) || 0
            }
          }
        })
        this.$bvToast.toast('Sensor settings saved.', {
          title: 'Saved',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error saving sensor settings:', err)
        this.$bvToast.toast('Failed to save sensor settings. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.page-heading {
  font-size: 1.1rem;
  vertical-align: middle;
}

.device-banner {
  background-color: #f8f9fa;
}

.banner-field {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}

.banner-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.05em;
}

.banner-value {
  font-weight: 600;
  font-size: 0.95rem;
}
</style>
